import React, { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import Fuse from 'fuse.js';
import { ngapCodes as medicalCodes, MedicalCode } from './data/MedicalCodes';

const normalize = (text: string): string =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const HISTORY_KEY = 'medicode_history';
const FAVORITES_KEY = 'medicode_favorites';
// ─── Valeurs TNR officielles ANAM (Tarif National de Référence) ──────────────
// Source : Grille n°1 ANAM + conventions CNSS 2020
const VALEURS_OFFICIELLES: Record<string, number> = {
  'K':    13.00,   // ANAM grille officielle
  'KC':   13.00,   // Même valeur que K
  'KE':   10.00,   // ANAM grille officielle
  'Z':     9.00,   // ANAM grille officielle
  'C':   150.00,   // CNSS 2020 (ancienne : 50 DH)
  'Cs':  250.00,   // CNSS 2020 (ancienne : 75 DH)
  'V':   150.00,   // Par référence à C
  'Vs':  250.00,   // Par référence à Cs
  'CNPSY': 100.00, // ANAM grille officielle
  'P':     0.90,   // ANAM grille officielle
  'D':    12.50,   // ANAM soins dentaires (prothèses : 17.50 DH)
  'SF':   40.00,   // ANAM grille officielle
  'SFI':  40.00,   // Par référence à SF
  'AMI':  40.00,   // ANAM grille officielle (par séance)
  'AMM':  40.00,   // ANAM grille officielle (par séance)
  'AMO':  40.00,   // ANAM grille officielle (par séance)
  'AMY':  40.00,   // ANAM grille officielle (par séance)
  'AMP':  40.00,   // ANAM grille officielle (par séance)
};

// ─── Règles officielles NGAP Maroc (Article 9) ───────────────────────────────
// Lettre-clés EXCLUES du cumul (Art.9 B §4) : Z, P
const LETTRES_HORS_CUMUL = ['Z','P'];

// Deviner la lettre-clé depuis le code d'acte (ex: "K50" → "K", "AMI3" → "AMI")
function detecterLettresCle(code: string): string {
  const lettres = Object.keys(VALEURS_OFFICIELLES).sort((a, b) => b.length - a.length);
  for (const l of lettres) {
    if (code.toUpperCase().startsWith(l.toUpperCase())) return l;
  }
  return 'K';
}

// Calculer le taux de cumul selon l'Article 9 B du NGAP marocain
// - 1er acte (le + cher) : 100%
// - 2ème acte : 50% (ou 75% si chirurgie traumatique multi-membres)
// - 3ème acte et suivants : 0% (sauf lésions traumatiques multiples → 50%)
// - Actes en Z et P : non soumis au cumul, toujours 100%
// - Consultations/Visites (C, Cs, V, Vs…) : non cumulables avec actes tech. (Art.9 A)
function calculerCumul(actes: MedicalCode[], traumatique: boolean = false): {
  item: MedicalCode;
  rang: number;           // 1 = principal, 2 = second, 3+ = non honoré
  taux: number;           // 1.0, 0.75, 0.5, ou 0
  horsRegle: boolean;     // true = hors règle cumul (Z, P)
  coeffEffectif: number;
}[] {
  if (actes.length === 0) return [];

  // Séparer les actes soumis au cumul des actes hors règle
  const soumis: MedicalCode[] = [];
  const horsRegle: MedicalCode[] = [];

  actes.forEach(a => {
    const l = detecterLettresCle(a.code);
    if (LETTRES_HORS_CUMUL.includes(l)) {
      horsRegle.push(a);
    } else {
      soumis.push(a);
    }
  });

  // Trier les actes soumis du + cher au - cher (Art.9 B §1)
  const sorted = [...soumis].sort((a, b) => (b.coefficient ?? 0) - (a.coefficient ?? 0));

  const resultats = sorted.map((item, index) => {
    let taux = 0;
    let rang = index + 1;

    if (index === 0) {
      taux = 1.0; // 1er acte : 100%
    } else if (index === 1) {
      // 2ème acte : 75% si chirurgie traumatique multi-membres, sinon 50%
      taux = traumatique ? 0.75 : 0.5;
    } else if (index === 2 && traumatique) {
      // 3ème acte : 50% uniquement en cas de lésions traumatiques multiples
      taux = 0.5;
    } else {
      taux = 0; // 3ème+ acte : non honoré
    }

    const coeffEffectif = Math.round((item.coefficient ?? 0) * taux * 100) / 100;
    return { item, rang, taux, horsRegle: false, coeffEffectif };
  });

  // Ajouter les actes hors règle (Z, P) toujours à 100%
  horsRegle.forEach(item => {
    resultats.push({
      item,
      rang: 0, // rang spécial = hors règle
      taux: 1.0,
      horsRegle: true,
      coeffEffectif: item.coefficient ?? 0,
    });
  });

  return resultats;
}

// ─── Couleurs par catégorie ───────────────────────────────────────────────────
const CATEGORY_COLORS: Record<string, string> = {
  'Fracture':        '#e3f2fd',
  'Radiologie':      '#f3e5f5',
  'Psychiatrie':     '#e8f5e9',
  'Gynécologie':     '#fce4ec',
  'Cardiologie interventionnelle': '#fff3e0',
  'Rééducation et réadaptation fonctionnelles': '#e0f7fa',
  'Soins et actes infirmiers': '#f9fbe7',
  'Dents et Gencives': '#fff8e1',
};
const DEFAULT_BADGE_COLOR = '#f0f4f8';

function getCategoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? DEFAULT_BADGE_COLOR;
}

function App() {
  const [searchInput, setSearchInput]       = useState('');
  const [selected, setSelected]             = useState<MedicalCode | null>(null);
  const [selectedCodes, setSelectedCodes]   = useState<MedicalCode[]>([]);
  const [history, setHistory]               = useState<MedicalCode[]>([]);
  const [favorites, setFavorites]           = useState<MedicalCode[]>([]);
  const [activeFilter, setActiveFilter]     = useState<string>('Tous');
  const [activeTab, setActiveTab]           = useState<'recherche' | 'favoris'>('recherche');
  const [valeurDH] = useState<number>(13.00);
  const [valeurD, setValeurD]               = useState<number>(17.50);

  const [showCategories, setShowCategories] = useState<boolean>(false);
  const [showInfo, setShowInfo]             = useState<boolean>(false);

  // ─── Fuse créé une seule fois ────────────────────────────────────────────────
  const fuse = useMemo(() => new Fuse(medicalCodes, {
    keys: ['code', 'description', 'categorie'],
    threshold: 0.4,
    includeScore: true,
    ignoreLocation: true,
    findAllMatches: true,
    useExtendedSearch: true,
  }), []);

  // ─── Catégories uniques ───────────────────────────────────────────────────────
  const categories = useMemo(() => {
    const cats = Array.from(new Set(medicalCodes.map(c => c.categorie))).sort();
    return ['Tous', ...cats];
  }, []);

  // ─── Résultats filtrés ────────────────────────────────────────────────────────
  const filteredCodes = useMemo(() => {
    let results: MedicalCode[] = searchInput
      ? fuse.search(normalize(searchInput)).map(r => r.item)
      : [];
    if (activeFilter !== 'Tous') {
      results = results.filter(r => r.categorie === activeFilter);
    }
    return results;
  }, [searchInput, activeFilter, fuse]);

  // ─── Chargement localStorage ──────────────────────────────────────────────────
  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY);
    if (savedHistory) setHistory(JSON.parse(savedHistory));
    const savedFavs = localStorage.getItem(FAVORITES_KEY);
    if (savedFavs) setFavorites(JSON.parse(savedFavs));}, []);

  // ─── Actions ──────────────────────────────────────────────────────────────────
  const handleSelect = (item: MedicalCode) => {
    setSelected(item);
    setSearchInput('');
    const newHistory = [item, ...history.filter(h => h.code !== item.code)].slice(0, 10);
    setHistory(newHistory);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
    if (!selectedCodes.find(c => c.code === item.code)) {
      setSelectedCodes(prev => [...prev, item]);
    }
  };

  const toggleFavorite = (item: MedicalCode, e: React.MouseEvent) => {
    e.stopPropagation();
    const isFav = favorites.find(f => f.code === item.code);
    const newFavs = isFav
      ? favorites.filter(f => f.code !== item.code)
      : [item, ...favorites];
    setFavorites(newFavs);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavs));
  };

  const isFavorite = (code: string) => !!favorites.find(f => f.code === code);

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
  };

  const totalCoeff = selectedCodes.reduce((sum, item) => sum + (item.coefficient ?? 0), 0);

  // ─── Calcul NGAP officiel avec règle de cumul (Art. 9) ──────────────────────
  const resultsCumul = useMemo(() => calculerCumul(selectedCodes, false), [selectedCodes]);
  const totalCoeffCumul = Math.round(resultsCumul.reduce((s, r) => s + r.coeffEffectif, 0) * 100) / 100;
  const totalDH = parseFloat((totalCoeffCumul * valeurDH).toFixed(2));




  // ─── Styles réutilisables ─────────────────────────────────────────────────────
  const btnStyle: React.CSSProperties = {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '18px', padding: '0 4px', lineHeight: 1,
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '10px', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 600, fontSize: '14px',
    background: active ? '#1997e6' : 'transparent',
    color: active ? '#fff' : '#888',
    transition: 'all 0.2s',
  });

  return (
    <>
      <div style={{
        background: 'linear-gradient(135deg, #58c6f1 0%, #4fb0e8 50%, #3a9fd8 100%)',
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '40px', paddingBottom: '80px',
        paddingLeft: '12px', paddingRight: '12px',
      }}>

        {/* ── Carte principale ── */}
        <div style={{
          maxWidth: '720px', width: '100%',
          background: '#fff', borderRadius: '24px',
          padding: '28px 24px', boxSizing: 'border-box',
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
        }}>

          {/* ── Modal Info ── */}
          {showInfo && (
            <div
              onClick={() => setShowInfo(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px',
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#fff', borderRadius: '20px',
                  padding: '28px 24px', maxWidth: '420px', width: '100%',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                }}
              >
                <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '12px' }}>🩺</div>
                <h2 style={{ textAlign: 'center', color: '#00394d', margin: '0 0 16px', fontSize: '20px' }}>
                  MediCode
                </h2>
                <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: '0 0 12px' }}>
                  <strong>MediCode</strong> est une application de recherche des actes médicaux codés selon la <strong>Nomenclature Générale des Actes Professionnels (NGAP)</strong> du Maroc.
                </p>
                <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: '0 0 12px' }}>
                  Elle permet de rechercher un acte par <strong>code</strong> ou <strong>description</strong>, de calculer les <strong>honoraires en dirhams</strong> et d'appliquer automatiquement la <strong>règle de cumul (Art. 9 NGAP)</strong> lors d'actes multiples dans la même séance.
                </p>
                <p style={{ fontSize: '13px', color: '#999', lineHeight: 1.6, margin: '0 0 20px' }}>
                  Conçue pour les médecins, internes et étudiants en santé au Maroc. Référence officielle : Arrêté n° 177-06 du 27 janvier 2006.
                </p>
                <button
                  onClick={() => setShowInfo(false)}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '12px',
                    background: '#1997e6', color: '#fff',
                    border: 'none', fontWeight: 700, fontSize: '14px', cursor: 'pointer',
                  }}
                >Fermer</button>
              </div>
            </div>
          )}

          {/* En-tête */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', position: 'relative' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'clamp(26px, 6vw, 34px)', fontWeight: 800, color: '#00394d' }}>
                🩺 MediCode
              </div>
              <p style={{ fontSize: '15px', color: '#666', margin: '6px 0 0' }}>
                Rechercher un acte médical par code ou description
              </p>
            </div>
            <button
              onClick={() => setShowInfo(true)}
              title="À propos de MediCode"
              style={{
                position: 'absolute', right: 0, top: 0,
                background: '#f0f4f8', border: 'none', borderRadius: '50%',
                width: '34px', height: '34px', cursor: 'pointer',
                fontSize: '15px', fontWeight: 700, color: '#1997e6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >ℹ</button>
          </div>

          {/* Onglets */}
          <div style={{
            display: 'flex', gap: '4px', background: '#f5f5f5',
            borderRadius: '12px', padding: '4px', marginBottom: '20px',
          }}>
            <button style={tabStyle(activeTab === 'recherche')} onClick={() => setActiveTab('recherche')}>
              🔍 Recherche
            </button>
            <button style={tabStyle(activeTab === 'favoris')} onClick={() => setActiveTab('favoris')}>
              ★ Favoris {favorites.length > 0 && `(${favorites.length})`}
            </button>
          </div>

          {/* ══ ONGLET RECHERCHE ══ */}
          {activeTab === 'recherche' && (
            <>
              {/* Champ de recherche */}
              <input
                type="text"
                placeholder="Ex : fracture, radio, D176, AMI…"
                value={searchInput}
                onChange={e => { setSearchInput(e.target.value); setSelected(null); setShowCategories(false); }}
                style={{
                  width: '100%', padding: '14px 16px', fontSize: '16px',
                  border: '2px solid #e0e0e0', borderRadius: '14px',
                  outline: 'none', boxSizing: 'border-box', marginBottom: '10px',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => (e.target.style.borderColor = '#1997e6')}
                onBlur={e => (e.target.style.borderColor = '#e0e0e0')}
              />

              {/* ── Bouton Catégorie ── */}
              {searchInput && (
                <div style={{ position: 'relative', marginBottom: '12px' }}>
                  <button
                    onClick={() => setShowCategories(v => !v)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '7px 14px', borderRadius: '20px', cursor: 'pointer',
                      border: activeFilter !== 'Tous' ? '2px solid #1997e6' : '1.5px solid #ddd',
                      background: activeFilter !== 'Tous' ? '#e8f4fd' : '#f8f8f8',
                      color: activeFilter !== 'Tous' ? '#1997e6' : '#555',
                      fontWeight: 600, fontSize: '13px',
                    }}
                  >
                    <span>🗂</span>
                    <span>{activeFilter !== 'Tous' ? activeFilter : 'Catégorie'}</span>
                    {activeFilter !== 'Tous' && (
                      <span
                        onClick={e => { e.stopPropagation(); setActiveFilter('Tous'); setShowCategories(false); }}
                        style={{ marginLeft: '4px', color: '#1997e6', fontWeight: 700, fontSize: '14px' }}
                      >×</span>
                    )}
                    <span style={{ marginLeft: '2px', fontSize: '10px' }}>{showCategories ? '▲' : '▼'}</span>
                  </button>

                  {/* Menu déroulant */}
                  {showCategories && (
                    <div style={{
                      position: 'absolute', top: '38px', left: 0, zIndex: 100,
                      background: '#fff', borderRadius: '14px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                      border: '1px solid #eee',
                      maxHeight: '260px', overflowY: 'auto',
                      minWidth: '220px', padding: '6px',
                    }}>
                      {categories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => { setActiveFilter(cat); setShowCategories(false); }}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '8px 12px', borderRadius: '8px',
                            border: 'none', cursor: 'pointer', fontSize: '13px',
                            background: activeFilter === cat ? '#e8f4fd' : 'transparent',
                            color: activeFilter === cat ? '#1997e6' : '#333',
                            fontWeight: activeFilter === cat ? 700 : 400,
                          }}
                        >
                          {activeFilter === cat && '✓ '}{cat}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Résultats ── */}
              {searchInput && filteredCodes.length > 0 && (
                <ul style={{
                  listStyle: 'none', padding: 0, margin: 0,
                  maxHeight: '300px', overflowY: 'auto',
                  borderRadius: '12px', border: '1px solid #eee',
                }}>
                  {filteredCodes.slice(0, 15).map(item => (
                    <li
                      key={item.code}
                      onClick={() => handleSelect(item)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '11px 14px', cursor: 'pointer',
                        borderBottom: '1px solid #f5f5f5',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fbff')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                    >
                      <div style={{ flex: 1 }}>
                        <span style={{
                          fontWeight: 700, color: '#1997e6',
                          marginRight: '8px', fontFamily: 'monospace', fontSize: '14px',
                        }}>{item.code}</span>
                        <span style={{ fontSize: '13px', color: '#333' }}>{item.description}</span>
                        <div style={{ marginTop: '4px' }}>
                          <span style={{
                            fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                            background: getCategoryColor(item.categorie), color: '#555',
                          }}>{item.categorie}</span>
                        </div>
                      </div>
                      <button
                        onClick={e => toggleFavorite(item, e)}
                        style={{ ...btnStyle, color: isFavorite(item.code) ? '#f5a623' : '#ccc' }}
                        title={isFavorite(item.code) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                      >
                        {isFavorite(item.code) ? '★' : '☆'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* ── Aucun résultat ── */}
              {searchInput && filteredCodes.length === 0 && (
                <div style={{
                  textAlign: 'center', padding: '30px 20px',
                  background: '#fafafa', borderRadius: '12px',
                  border: '1px dashed #ddd',
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                  <div style={{ fontWeight: 600, color: '#444', marginBottom: '4px' }}>
                    Aucun acte trouvé
                  </div>
                  <div style={{ fontSize: '13px', color: '#888' }}>
                    Essayez un autre terme ou changez de filtre de catégorie
                  </div>
                  {activeFilter !== 'Tous' && (
                    <button
                      onClick={() => setActiveFilter('Tous')}
                      style={{
                        marginTop: '12px', padding: '7px 16px',
                        background: '#1997e6', color: '#fff',
                        border: 'none', borderRadius: '8px',
                        cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                      }}
                    >
                      Voir tous les résultats
                    </button>
                  )}
                </div>
              )}

              {/* ── Détail acte sélectionné ── */}
              {selected && !searchInput && (
                <div style={{
                  marginTop: '16px', padding: '16px',
                  background: '#f0faff', borderRadius: '12px',
                  border: '1px solid #b3e0f7',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '20px', color: '#1997e6', marginBottom: '4px' }}>
                        {selected.code}
                      </div>
                      <div style={{ fontSize: '14px', color: '#333', marginBottom: '6px' }}>
                        {selected.description}
                      </div>
                      <span style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                        background: getCategoryColor(selected.categorie), color: '#555',
                      }}>{selected.categorie}</span>
                    </div>
                    <div style={{
                      textAlign: 'center', marginLeft: '16px',
                      background: '#1997e6', color: '#fff',
                      borderRadius: '10px', padding: '8px 14px', minWidth: '60px',
                    }}>
                      <div style={{ fontSize: '22px', fontWeight: 800 }}>{selected.coefficient ?? '—'}</div>
                      <div style={{ fontSize: '10px', opacity: 0.85 }}>coefficient</div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Historique ── */}
              {!searchInput && history.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontWeight: 700, color: '#444', fontSize: '14px' }}>
                      🕐 Dernières recherches
                    </span>
                    <button onClick={clearHistory} style={{
                      background: 'none', border: 'none', color: '#aaa',
                      cursor: 'pointer', fontSize: '12px',
                    }}>Effacer</button>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {history.map(item => (
                      <li
                        key={item.code}
                        onClick={() => handleSelect(item)}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '9px 0', borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer', fontSize: '13px', color: '#444',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#1997e6')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#444')}
                      >
                        <span>
                          <strong style={{ color: '#1997e6', fontFamily: 'monospace' }}>{item.code}</strong>
                          {' – '}{item.description}
                        </span>
                        <button onClick={e => toggleFavorite(item, e)} style={{ ...btnStyle, color: isFavorite(item.code) ? '#f5a623' : '#ddd' }}>
                          {isFavorite(item.code) ? '★' : '☆'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* ══ ONGLET FAVORIS ══ */}
          {activeTab === 'favoris' && (
            <div>
              {favorites.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#aaa' }}>
                  <div style={{ fontSize: '40px', marginBottom: '10px' }}>☆</div>
                  <div style={{ fontWeight: 600, fontSize: '15px', color: '#888' }}>
                    Aucun favori pour l'instant
                  </div>
                  <div style={{ fontSize: '13px', marginTop: '6px' }}>
                    Appuyez sur ★ sur un acte pour l'ajouter ici
                  </div>
                </div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {favorites.map(item => (
                    <li
                      key={item.code}
                      onClick={() => handleSelect(item)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '11px 0', borderBottom: '1px solid #f5f5f5',
                        cursor: 'pointer',
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 700, color: '#1997e6', fontFamily: 'monospace', marginRight: '8px' }}>
                          {item.code}
                        </span>
                        <span style={{ fontSize: '13px', color: '#333' }}>{item.description}</span>
                        <div style={{ marginTop: '3px' }}>
                          <span style={{
                            fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                            background: getCategoryColor(item.categorie), color: '#555',
                          }}>{item.categorie}</span>
                        </div>
                      </div>
                      <button onClick={e => toggleFavorite(item, e)} style={{ ...btnStyle, color: '#f5a623' }}>★</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ── Actes sélectionnés (session) ── */}
          {selectedCodes.length > 0 && (
            <div style={{
              marginTop: '24px', padding: '16px',
              background: '#f8f8f8', borderRadius: '14px',
              border: '1px solid #e8e8e8',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: 700, color: '#444', fontSize: '14px' }}>
                  📋 Actes sélectionnés ({selectedCodes.length})
                </span>
                <button onClick={() => setSelectedCodes([])} style={{
                  background: 'none', border: 'none', color: '#aaa',
                  cursor: 'pointer', fontSize: '12px',
                }}>Tout effacer</button>
              </div>

              {/* ── Liste des actes avec règle de cumul ── */}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {resultsCumul.map((r, index) => {
                  const isD = r.item.categorie === 'Dents et Gencives';
                  const isTrauma = r.item.code.toUpperCase().startsWith('A');
                  const montantDH = parseFloat((r.coeffEffectif * (isD ? valeurD : valeurDH)).toFixed(2));
                  const tagColor =
                    r.horsRegle ? { bg: '#e8f5e9', text: '#2e7d32', label: 'Hors cumul (100%)' } :
                    r.rang === 1 ? { bg: '#e3f2fd', text: '#1565c0', label: '1er acte · 100%' } :
                    r.rang === 2 ? { bg: '#fff8e1', text: '#f57f17', label: '2ème acte · 50%' } :
                    { bg: '#fce4ec', text: '#c62828', label: 'Non honoré · 0%' };

                  return (
                    <li key={`${r.item.code}-${index}`} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 0', borderBottom: '1px solid #eee', fontSize: '13px',
                      opacity: r.taux === 0 ? 0.5 : 1,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div>
                          <strong style={{ color: '#1997e6', fontFamily: 'monospace' }}>{r.item.code}</strong>
                          <span style={{ color: '#333' }}> – {r.item.description}</span>
                        </div>
                        {isTrauma && (
                          <span style={{
                            fontSize: '10px', padding: '1px 7px', borderRadius: '8px',
                            background: tagColor.bg, color: tagColor.text,
                            fontWeight: 600, marginTop: '3px', display: 'inline-block',
                          }}>
                            {tagColor.label}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', margin: '0 10px', minWidth: '90px' }}>
                        <span style={{ fontSize: '11px', color: '#999', textDecoration: r.taux < 1 && !r.horsRegle ? 'line-through' : 'none' }}>
                          coeff. {r.item.coefficient ?? '—'}
                        </span>
                        {r.taux > 0 && (
                          <>
                            <span style={{ fontWeight: 700, color: '#1997e6', fontSize: '13px' }}>
                              = {r.coeffEffectif}
                            </span>
                            <span style={{ fontSize: '12px', color: '#2ecc71', fontWeight: 600 }}>
                              {montantDH.toLocaleString('fr-MA')} DH
                            </span>
                          </>
                        )}
                        {r.taux === 0 && (
                          <span style={{ fontSize: '12px', color: '#e74c3c', fontWeight: 600 }}>Non facturé</span>
                        )}
                      </div>
                      <button
                        onClick={() => setSelectedCodes(prev => prev.filter(c => c.code !== r.item.code))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '16px' }}
                      >✕</button>
                    </li>
                  );
                })}
              </ul>

              {/* ── Boutons dentaires ── */}
              {selectedCodes.some(c => c.categorie === 'Dents et Gencives') && (
                <div style={{
                  marginTop: '10px', padding: '10px 12px',
                  background: '#fff8e1', borderRadius: '8px',
                  border: '1px solid #ffe082',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#856404', marginBottom: '8px' }}>
                    🦷 Acte dentaire — choisissez le type :
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setValeurD(12.50)}
                      style={{
                        flex: 1, padding: '8px', borderRadius: '8px',
                        fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        border: valeurD === 12.50 ? '2px solid #f59e0b' : '1.5px solid #ddd',
                        background: valeurD === 12.50 ? '#f59e0b' : '#fff',
                        color: valeurD === 12.50 ? '#fff' : '#444',
                      }}
                    >
                      🦷 Soins<br/>
                      <span style={{ fontSize: '13px', fontWeight: 800 }}>12.50 DH</span>
                    </button>
                    <button
                      onClick={() => setValeurD(17.50)}
                      style={{
                        flex: 1, padding: '8px', borderRadius: '8px',
                        fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        border: valeurD === 17.50 ? '2px solid #f59e0b' : '1.5px solid #ddd',
                        background: valeurD === 17.50 ? '#f59e0b' : '#fff',
                        color: valeurD === 17.50 ? '#fff' : '#444',
                      }}
                    >
                      🔧 Prothèses<br/>
                      <span style={{ fontSize: '13px', fontWeight: 800 }}>17.50 DH</span>
                    </button>
                  </div>
                </div>
              )}

              {/* ── Total ── */}
              <div style={{
                marginTop: '10px', padding: '14px 16px',
                background: 'linear-gradient(135deg, #1997e6, #0f7bc4)',
                borderRadius: '12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
                    Coefficients bruts (sans règle)
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '13px' }}>{totalCoeff}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 600 }}>
                    Coefficients après cumul NGAP (Art.9)
                  </span>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '16px' }}>{totalCoeffCumul}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.25)', paddingTop: '10px' }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>Total à facturer</span>
                  <span style={{ color: '#fff', fontWeight: 800, fontSize: '26px' }}>
                    {totalDH.toLocaleString('fr-MA')} <span style={{ fontSize: '14px' }}>DH</span>
                  </span>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', margin: '6px 0 0', textAlign: 'right' }}>
                  Règle de cumul NGAP · {valeurDH} DH / coefficient
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── Légende lettres-clés ── */}
        <div style={{
          maxWidth: '720px', width: '100%', marginTop: '20px',
          background: 'rgba(255,255,255,0.15)', borderRadius: '16px',
          padding: '16px 20px', boxSizing: 'border-box',
        }}>
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#fff', fontSize: '14px', marginBottom: '8px' }}>
              📖 Légende des lettres-clés
            </summary>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px', marginTop: '10px' }}>
              {Object.entries({
                'C': 'Consultation généraliste',
                'Cs': 'Consultation spécialiste',
                'CNPSY': 'Consultation psychiatre / neurologue',
                'V': 'Visite généraliste',
                'Vs': 'Visite spécialiste',
                'VNPSY': 'Visite psychiatre / neurologue',
                'K': 'Actes de chirurgie',
                'KC': 'Actes de chirurgie',
                'KE': 'Échographie ou doppler',
                'P': 'Anatomie pathologique',
                'Z': 'Radiations ionisantes',
                'D': 'Actes dentaires',
                'SF': 'Actes sage-femme',
                'SFI': 'Soins infirmiers (sage-femme)',
                'AMI': 'Actes infirmiers',
                'AMM': 'Actes kinésithérapeutes',
                'AMP': 'Actes pédicure',
                'AMO': 'Actes orthophonistes',
                'AMY': 'Actes orthoptistes',
              }).map(([lettre, desc]) => (
                <div key={lettre} style={{ fontSize: '12px', color: '#fff' }}>
                  <strong>{lettre}</strong> : {desc}
                </div>
              ))}
            </div>
          </details>
        </div>

        {/* ── Footer légal ── */}
        <div style={{
          maxWidth: '720px', width: '100%',
          marginTop: '24px', paddingBottom: '30px',
          textAlign: 'center',
        }}>
          {/* Disclaimer médical */}
          <div style={{
            background: 'rgba(255,255,255,0.15)', borderRadius: '12px',
            padding: '12px 16px', marginBottom: '16px',
            fontSize: '11px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6,
          }}>
            ⚕️ <strong>Avertissement :</strong> MediCode est un outil de référence à usage informatif uniquement.
            Il ne constitue pas un avis médical, un diagnostic ou une prescription.
            Consultez toujours un professionnel de santé qualifié pour toute décision médicale.
            Les tarifs affichés sont des estimations — ils peuvent varier selon l'établissement.
          </div>

          {/* Liens légaux */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {[
              { label: 'Politique de confidentialité', href: '/privacy.html' },
              { label: 'Mentions légales', href: '/legal.html' },
              { label: 'Contact', href: 'mailto:contact@medicodeapp.org' },
            ].map(link => (
              <a key={link.label} href={link.href} style={{
                color: 'rgba(255,255,255,0.85)', fontSize: '12px',
                textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.3)',
                paddingBottom: '1px',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.85)')}
              >
                {link.label}
              </a>
            ))}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
            © {new Date().getFullYear()} MediCode · medicodeapp.org · Tous droits réservés
          </p>
        </div>
      </div>
    </>
  );
}

export default App;

